<template>
    <div class="__page">
        <div class="placeholder">
            <CampaignHeader
                :image-url="campaignHeader.imageUrl"
                :body-html="campaignHeader.bodyHtml"
            />
            <ConsentFull
                v-if="isConsentsFull"
                :icons="['email', 'phone', 'sms']"
                body-text="Dziękujemy za wyrażenie wszystkich zgód!"
            />
            <ConsentGroup :is-label-enabled="pendingConsents.length > 0">
                <Consent
                    v-if="leafletConsent !== null"
                    label="Rezygnuję z gazetki papierowej"
                    control-label="Wyrażam zgodę"
                    icon-type="leaflet"
                    theme="special"
                    :is-inputless="true"
                />
                <Consent
                    v-for="consent in pendingConsents"
                    ref="consent"
                    :key="consent.type"
                    :type="consent.type"
                    :label="consent.label"
                    :description="consent.description"
                    :icon-type="consent.name"
                    :input-label="consent.input.longLabel"
                    :input-type="consent.input.type"
                />
            </ConsentGroup>

            <div class="wrapper">
                <Expandable
                    :label="expandable.label"
                    :intro-text="expandable.introText"
                >
                    <Disclaimer />
                </Expandable>
                <BannerApp
                    :text="bannerApp.text"
                />
            </div>

            <div :class="['area-disabled', { 'is-active': isManagerOpen }]" />
        </div>

        <div class="wrapper element-relative">
            <ButtonSave
                :is-disabled="!isSaveButtonEnabled"
                :theme="['right', 'main']"
                label-html="Zapisz zmiany"
                @click.native="onClickSave"
            />
            <ConsentManager
                v-if="!isSamplingCampaign"
                ref="consentManager"
                :is-disabled="!isConsentsManageable"
                :items="menageableConsents"
                @open="onOpenManager"
                @save="onSaveOldConsents"
            />
            <div
                v-else
                class="placeholder placeholder-125"
            />
        </div>

        <Modal ref="modal" />
        <Modal
            :is-closeable="true"
            closeLabel="Zamknij okno"
            ref="modalError"
        />
    </div>
</template>

<script>
import BannerApp from '@/components/banner/BannerApp'
import ButtonSave from '@/components/button/ButtonSave'
import CampaignHeader from '@/components/campaign/CampaignHeader'
import Consent from '@/components/consent/Consent'
import ConsentFull from '@/components/consent/ConsentFull'
import ConsentGroup from '@/components/consent/ConsentGroup'
import ConsentManager from '@/components/consent/ConsentManager'
import ConsentMapper from '@/mapper/ConsentMapper'
import Disclaimer from '@/components/disclaimer/Disclaimer'
import Expandable from '@/components/expandable/Expandable'
import Modal from '@/components/modal/Modal'
import SamplingService from '@/service/SamplingService'
import VueScrollTo from 'vue-scrollto'

export default {
    components: {
        BannerApp,
        ButtonSave,
        CampaignHeader,
        Consent,
        ConsentFull,
        ConsentGroup,
        ConsentManager,
        Disclaimer,
        Expandable,
        Modal
    },

    data () {
        return {
            expandable: {
                label: 'Nota prawna',
                introText: 'Wyrażam zgodę na przetwarzanie moich danych osobowych przez MAKRO Cash and Carry Polska S.A.'
            },
            bannerApp: {
                text: '<p><strong>Ściągnij aplikację mobilną</strong></p><p>Sprawdzaj oferty i korzystaj z rabatów tylko dla posiadaczy apki.</p>'
            },
            modal: {
                bodyHtml: `
                    <header class="fs-32 text-bold">
                        Jesteś pewien?
                    </header>
                    <p class="fs-18 fs-lh-28">
                        Pamiętaj, że dzięki zgodom na komunikację <strong>będziesz na bieżąco ze wszystkimi ofertami specjalnymi MAKRO.</strong><br>
                        A co tydzień otrzymasz od nas <strong>specjalne i dedykowane dla Ciebie oferty</strong>
                    </p>
                `
            },
            isManagerOpen: false
        }
    },

    computed: {
        campaignData() {
            return this.$store.getters['apiCampaign/campaign']
        },

        campaignHeader() {
            return {
                imageUrl: this.campaignData.imageStep2,
                bodyHtml: this.campaignData.benefits2
            }
        },

        isSaveButtonEnabled() {
            return (this.$store.getters['consentsResolver/isPendingChanged'] || this.isConsentsFull) && !this.isManagerOpen
        },

        consentsStructure() {
            return this.$store.getters['consentsResolver/consentsStructure']
        },

        pendingConsents() {
            if (!this.consentsStructure.pending) {
                return []
            }

            const input = this.consentsStructure.pending

            return input.map(item => ConsentMapper.getByType(item.type))
         },

        menageableConsents() {
            if (!this.consentsStructure.manageable) {
                return []
            }

            const input = this.consentsStructure.manageable

            return input.map(item => {
                const map = ConsentMapper.getByType(item.type)

                return {
                    label: map.label,
                    inputLabel: map.input.shortLabel,
                    inputType: map.input.type,
                    inputValue: item.value,
                    isGranted: item.isGranted,
                    name: map.name,
                    type: item.type
                }
            })
        },

        leafletConsent() {
            return this.consentsStructure.leaflet
        },

        isConsentsFull() {
            return this.consentsStructure.pending
                ? this.consentsStructure.pending.length === 0 && this.consentsStructure.manageable.filter(item => item.isGranted !== true).length === 0
                : false
        },

        isConsentsManageable() {
            return this.consentsStructure.manageable ? this.consentsStructure.manageable.length !== 0 : false
        },

        customerData() {
            return this.$store.getters['apiCustomer/fullData']
        },

        isSamplingCampaign() {
            return SamplingService.isSamplingCampaign(this.$store)
        }
    },

    mounted() {
        this.$store.dispatch('steps/intendConsents')
    },

    methods: {
        onOpenManager(value) {
            this.isManagerOpen = value
        },

        onClickSave() {
            let consentsValidated = true

            if (this.$refs.consent) {
                for (let i = 0; i < this.$refs.consent.length; i++) {
                    const itemValidated = this.$refs.consent[i].validate()

                    consentsValidated = consentsValidated && itemValidated
                }

                if (!consentsValidated) {
                    VueScrollTo.scrollTo('.campaign-header', 800)

                    return
                }
            }

            // @TODO: payload = helper.prepearePayload(consetnsStructure) // === null if no changes applied
            const consentsStructure = this.$store.getters['consentsResolver/consentsStructure']
            const pendingConsents = consentsStructure.pending.filter(item => item.isGranted === true)
            //  const manageableConsents = consentsStructure.manageable
            const leafletConsent = consentsStructure.leaflet

            if (pendingConsents.length === 0 && leafletConsent !== true && this.isConsentsFull !== true) {
                console.warn('No consents given, cannot submit form')

                return
            }

            const payload = {
                "marketingConsents": pendingConsents.map(item => {
                    return {
                        type: item.type,
                        value: item.value,
                        statusCd: item.isGranted === true ? 1 : 0,
                        resignationReason: "",
                        resignationReasonOther: ""
                    }
                }),
                "paperBrochure": leafletConsent !== false
            }

            this.handleSaveRequest(payload, async response => {
                this.$store.dispatch('coupon/setCoupon', {
                    type: 'coupon',
                    pdf: response.data.pdf || '',
                    image: response.data.image,
                    sendEmailUrl: response.data.sendEmailUrl
                })

                if (this.$store.getters['apiCampaign/isSampling']) {
                    try {
                        await this.checkSample()
                    }
                    catch(e) {
                        console.error(e)
                    }
                }

                this.$router.push({ name: 'kupon' })
            })
        },

        onSaveOldConsents() {
            if (!this.$refs.consentManager.validate()) {
                VueScrollTo.scrollTo('.consent-manager', 800)

                return
            }

            // @TODO: payload = helper.prepearePayload(consetnsStructure) // === null if no changes applied
            const hasResignations = this.$store.getters['consentsResolver/hasResignations']
            const consentsStructure = this.$store.getters['consentsResolver/consentsStructure']
            const manageableConsents = consentsStructure.manageable
            const leafletConsent = consentsStructure.leaflet

            const payload = {
                "marketingConsents": manageableConsents.map(item => {
                    return {
                        type: item.type,
                        value: item.value,
                        statusCd: item.isGranted === true ? 1 : 0,
                        resignationReason: item.resignationReason,
                        resignationReasonOther: item.resignationReasonOther
                    }
                }),
                "paperBrochure": leafletConsent === null
            }

            if (hasResignations) {
                this.$refs.modal.open(this.modal.bodyHtml, 'center', () => {
                    this.handleSaveRequest(payload, async () => {
                        this.$store.dispatch('coupon/setCoupon', null)
                        this.$router.push({ name: 'kupon' })
                    })
                }, () => {
                    this.$store.dispatch('consentsResolver/restoreAllManageableConsents')
                })
            }
            else {
                this.handleSaveRequest(payload, async response => {
                    this.$store.dispatch('coupon/setCoupon', {
                        type: 'coupon',
                        pdf: response.data.pdf || '',
                        image: response.data.image,
                        sendEmailUrl: response.data.sendEmailUrl
                    })

                    this.$router.push({ name: 'kupon' })
                })
            }
        },

        handleSaveRequest(payload, asyncCallback) {
            console.log('[SAVE CONSENTS PAYLOAD]')
            console.log(payload)

            this.$store.dispatch('siteLoader/open')

            this.$store.dispatch('apiCustomer/saveMarketingConsents', payload)
                .then(async response => {
                    console.log(response)
                    await asyncCallback(response)
                })
                .catch(e => {
                    this.$refs.modalError
                        .open(`<header class="fs-32">Wystąpił błąd podczas zapisywania danych.</header><p class="fs-22">Spróbuj ponownie później.</p>`, 'center-small', function() {

                        })

                    console.error(e.response)
                })
                .finally(() => {
                    this.$store.dispatch('siteLoader/close')
                })
        },

        checkSample() {
            const self = this

            return this.$store.dispatch('apiSample/checkSample', {
                sample: self.$store.getters['apiSample/selectedSampleId'],
                partCard1: self.customerData.customerData.partCard1,
                partCard2: self.customerData.customerData.partCard2,
                partCard3: self.customerData.customerData.partCard3,
                firstName: self.customerData.customerData.firstName,
                lastName: self.customerData.customerData.lastName
            })
        }
    }
}
</script>

<style>

</style>
